import React from "react";

import { isValid } from "date-fns";
import { FieldPath, FieldValues } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { DatePicker, DatePickerProps, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

import { CalendarIcon } from "@/assets/icons";
import { convertDateToFullYear, dateLocales, resetTime } from "@/core/date";
import { defaultMaxDate, defaultMinDate } from "@/core/yup";

import ControllerInput, { ControllerInputProps } from "../ControllerInput/ControllerInput";

export type DateInputProps<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<ControllerInputProps<TFieldValues, TName>, "render"> &
    DatePickerProps<Date> & {
        placeholder?: string;
    };

const DateInput = <
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
    name,
    control,
    defaultValue = null,
    FormControlProps,
    LabelProps,
    hideErrorMessage,
    label,
    fullWidth,
    placeholder,
    required = false,
    minDate = defaultMinDate,
    maxDate = defaultMaxDate,
    openTo = "day",
    views = ["year", "month", "day"],
    format = "dd/MM/yyyy",
    ...dateInputProps
}: DateInputProps<TFieldValues, TName>): React.ReactElement => {
    const { i18n, t } = useTranslation();

    return (
        <ControllerInput
            control={control}
            name={name}
            defaultValue={defaultValue}
            label={label}
            LabelProps={LabelProps}
            FormControlProps={FormControlProps}
            required={required}
            fullWidth={fullWidth}
            hideErrorMessage={hideErrorMessage}
            render={({ field: { onChange, onBlur, value, ref }, error }) => (
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={dateLocales[i18n.language]}>
                    <DatePicker
                        inputRef={ref}
                        closeOnSelect
                        openTo={openTo}
                        views={views}
                        minDate={minDate}
                        maxDate={maxDate}
                        format={format}
                        localeText={{ toolbarTitle: "" }}
                        onChange={onChange}
                        value={value}
                        slotProps={{
                            toolbar: { hidden: true },
                            textField: {
                                id: name,
                                name,
                                fullWidth,
                                placeholder: placeholder || t("Common.DatePlaceholder"),
                                error: !!error,
                                onBlur: () => {
                                    let date = value as Date;

                                    if (date) {
                                        if (isValid(value) && value.getFullYear() < 100) {
                                            date = convertDateToFullYear(value);
                                        }

                                        onChange(resetTime(date));
                                    } else {
                                        onChange(null);
                                    }

                                    onBlur();
                                },
                                inputProps: { "data-testid": `date_input-${name}` },
                            },
                        }}
                        slots={{ openPickerIcon: CalendarIcon }}
                        onAccept={onBlur}
                        onClose={onBlur}
                        {...dateInputProps}
                    />
                </LocalizationProvider>
            )}
        />
    );
};

export default DateInput;
